/**
 * Phase 169 — Broadcast Composer Auto-Pilot Panel
 *
 * One-click emergency dispatch. Lists saved broadcast templates with severity
 * + targeting metadata and lets ops fire either a dry-run preview or a live
 * blast. Mounted at the top of `/super-admin/announce`.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Rocket, ShieldAlert, CloudRain, ThermometerSun, Trophy, Wrench, BookOpen,
  Megaphone, Send, Loader2, Eye, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_MAP = {
  'cloud-rain': CloudRain,
  'thermometer-sun': ThermometerSun,
  'trophy': Trophy,
  'wrench': Wrench,
  'book-open': BookOpen,
  'megaphone': Megaphone,
};

const SEVERITY_COLOR = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  warning:  'bg-amber-100 text-amber-800 border-amber-300',
  info:     'bg-blue-100 text-blue-800 border-blue-300',
};

const TARGET_LABEL = {
  all: 'All academies',
  states: 'States',
  cities: 'Cities',
  academies: 'Academies',
};

export default function AutoPilotPanel({ onDispatch }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTpl, setConfirmTpl] = useState(null);
  const [topicOverride, setTopicOverride] = useState('');
  const [firing, setFiring] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/broadcast/templates');
      setTemplates((r.data?.templates || []).filter((t) => !t.hidden));
    } catch (err) {
      toast.error('Could not load auto-pilot templates');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPreview = async (tpl) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const r = await api.post('/admin/broadcast/autopilot-dispatch', {
        template_id: tpl.id,
        dry_run: true,
      });
      setPreviewData({ template: tpl, ...r.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openConfirm = (tpl) => {
    setConfirmTpl(tpl);
    setTopicOverride('');
    setConfirmOpen(true);
  };

  const fire = async () => {
    if (!confirmTpl) return;
    setFiring(true);
    try {
      const body = { template_id: confirmTpl.id, dry_run: false };
      const t = topicOverride.trim();
      if (t) body.topic_hint_override = t;
      const r = await api.post('/admin/broadcast/autopilot-dispatch', body);
      if (r.data?.success) {
        toast.success(
          `🚀 Auto-Pilot fired "${confirmTpl.name}" — ${r.data?.delivery?.total_recipients ?? 0} recipients across ${r.data?.delivery?.total_academies ?? 0} academies.`
        );
        setConfirmOpen(false);
        if (onDispatch) onDispatch();
      } else {
        toast.warning(r.data?.message || 'Auto-Pilot returned no recipients');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Auto-Pilot dispatch failed');
    } finally {
      setFiring(false);
    }
  };

  const targetingSummary = (tpl) => {
    if (tpl.target_mode === 'all') return 'All academies';
    if (tpl.target_mode === 'states') return tpl.target_states?.join(', ') || 'No states';
    if (tpl.target_mode === 'cities') return tpl.target_cities?.join(', ') || 'No cities';
    if (tpl.target_mode === 'academies') return `${tpl.target_academy_ids?.length || 0} academies`;
    return '';
  };

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50/40 via-white to-orange-50/30"
      data-testid="phase169-autopilot-panel"
    >
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Rocket className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-bold text-base sm:text-lg text-slate-900" data-testid="phase169-title">
                  Broadcast Composer Auto-Pilot
                </h2>
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] font-bold uppercase tracking-widest">Phase 169</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">
                One-click emergency dispatch. The AI Copilot drafts the message, the platform
                handles fan-out across every channel in under 30 seconds.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-amber-700 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="phase169-template-grid">
            {templates.map((tpl) => {
              const Icon = ICON_MAP[tpl.icon] || Megaphone;
              return (
                <div
                  key={tpl.id}
                  className="bg-white rounded-xl border border-slate-200 p-3.5 hover:border-amber-300 hover:shadow-md transition-all"
                  data-testid={`phase169-template-${tpl.id}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-amber-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900 leading-tight truncate">
                        {tpl.name}
                      </p>
                      <Badge
                        variant="outline"
                        className={`mt-1 text-[9px] py-0 ${SEVERITY_COLOR[tpl.severity] || SEVERITY_COLOR.info}`}
                      >
                        {tpl.severity === 'critical' && <AlertTriangle className="h-2 w-2 mr-0.5" />}
                        {tpl.severity?.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-snug line-clamp-2">
                    {tpl.ai_context_prompt?.slice(0, 100)}…
                  </p>
                  <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-[9px] py-0">
                      {TARGET_LABEL[tpl.target_mode]}
                    </Badge>
                    <span className="truncate flex-1 min-w-0">{targetingSummary(tpl)}</span>
                  </div>
                  <div className="mt-2 text-[10px] text-slate-400 flex items-center gap-1.5">
                    {(tpl.channels || []).map((ch) => (
                      <Badge key={ch} variant="outline" className="text-[8px] py-0 capitalize">{ch}</Badge>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openPreview(tpl)}
                      className="h-7 text-[10px] flex-1"
                      data-testid={`phase169-preview-${tpl.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" /> Preview
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openConfirm(tpl)}
                      className="h-7 text-[10px] flex-1 bg-amber-600 hover:bg-amber-700"
                      data-testid={`phase169-fire-${tpl.id}`}
                    >
                      <Rocket className="h-3 w-3 mr-1" /> Fire
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-auto"
          data-testid="phase169-preview-modal">
          <DialogHeader>
            <DialogTitle>Auto-Pilot Preview</DialogTitle>
            <DialogDescription>
              {previewData?.template?.name || 'Generating draft…'}
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              AI Copilot drafting…
            </div>
          ) : previewData ? (
            <div className="space-y-3 text-sm">
              <Badge
                variant="outline"
                className={SEVERITY_COLOR[previewData.template?.severity] || SEVERITY_COLOR.info}
              >
                {previewData.template?.severity?.toUpperCase()}
              </Badge>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Title</Label>
                <p className="font-bold">{previewData.preview?.title}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Message</Label>
                <p className="whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded p-3 max-h-72 overflow-auto">
                  {previewData.preview?.message}
                </p>
              </div>
              <div className="text-[10px] text-slate-400 flex items-center gap-2">
                <Badge variant="outline" className="text-[9px] py-0">
                  Draft source: {previewData.draft_source}
                </Badge>
                <Badge variant="outline" className="text-[9px] py-0">
                  Target: {TARGET_LABEL[previewData.template?.target_mode]}
                </Badge>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => { setPreviewOpen(false); openConfirm(previewData.template); }}
                  data-testid="phase169-preview-fire-from-here"
                >
                  <Rocket className="h-3.5 w-3.5 mr-1" /> Fire this template
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirm-fire modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="phase169-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Confirm Auto-Pilot Dispatch
            </DialogTitle>
            <DialogDescription>
              You're about to fire <strong>{confirmTpl?.name}</strong>. The AI Copilot will draft
              the message and the platform will dispatch immediately on every configured channel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px]">
              <div><strong>Target:</strong> {TARGET_LABEL[confirmTpl?.target_mode]}</div>
              <div><strong>Severity:</strong> {confirmTpl?.severity?.toUpperCase()}</div>
              <div><strong>Channels:</strong> {(confirmTpl?.channels || []).join(' + ')}</div>
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Topic hint <span className="font-normal text-slate-400">(optional)</span>
              </Label>
              <Input
                placeholder="e.g. red alert escalated by IMD at 14:00 IST"
                value={topicOverride}
                onChange={(e) => setTopicOverride(e.target.value)}
                className="mt-1"
                data-testid="phase169-topic-override"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={firing}>Cancel</Button>
              <Button
                onClick={fire} disabled={firing}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="phase169-fire-confirm"
              >
                {firing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Fire Auto-Pilot
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
